import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'
import PrintPreviewModal from '../../components/PrintPreviewModal'

interface Supplier {
  id: string
  name: string
  supplierCode?: string
  companyName?: string
  email?: string
  phone?: string
  address?: string
  taxId?: string
  paymentTerms?: string
  currency?: string
}

interface PurchaseInvoice {
  id: string
  invoiceNumber: string
  invoiceDate: string
  dueDate: string
  postingDate?: string
  supplier: string
  supplierId?: string
  supplierAddress?: string
  supplierTaxId?: string
  supplierRecord?: Supplier
  status: string
  subtotal: string
  taxAmount: string
  total: string
  paidAmount: string
  outstandingAmount: string
  currency?: string
  notes?: string
  terms?: string
  paymentTerms?: string
  items?: PurchaseInvoiceItemDetail[]
}

interface PurchaseInvoiceItemDetail {
  id: string
  description: string
  quantity: string
  unitPrice: string
  taxAmount: string
  total: string
  account?: {
    code: string
    name: string
  }
  taxRate?: {
    name: string
    rate: string
  }
}

interface Account {
  id: string
  code: string
  name: string
  rootType: string
}

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
}

interface PurchaseInvoiceItem {
  description: string
  quantity: string
  unitPrice: string
  accountId: string
  taxRateId: string
}

const PurchaseInvoices: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [purchaseInvoices, setPurchaseInvoices] = useState<PurchaseInvoice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showPrintPreview, setShowPrintPreview] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseInvoice | null>(null)
  const [editingInvoice, setEditingInvoice] = useState<PurchaseInvoice | null>(null)
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [formData, setFormData] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    postingDate: new Date().toISOString().split('T')[0],
    supplier: '',
    supplierId: '',
    supplierAddress: '',
    supplierTaxId: '',
    currency: 'GBP',
    notes: '',
    terms: '',
    paymentTerms: '',
    items: [
      { description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '' },
    ] as PurchaseInvoiceItem[],
  })

  useEffect(() => {
    fetchPurchaseInvoices()
    fetchAccounts()
    fetchTaxRates()
    fetchSuppliers()
  }, [])

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/api/erp/suppliers', {
        params: { includeInactive: 'false' },
      })
      setSuppliers(response.data.data || [])
    } catch (error) {
      console.error('Error fetching suppliers:', error)
    }
  }

  const fetchPurchaseInvoices = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/purchase-invoices')
      setPurchaseInvoices(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching purchase invoices:', error)
      toast.error('Failed to load purchase invoices')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', isGroup: 'false' } })
      // Filter for EXPENSE or ASSET accounts only
      const filtered = (response.data.data || []).filter((acc: Account) => 
        acc.rootType === 'EXPENSE' || acc.rootType === 'ASSET'
      )
      setAccounts(filtered)
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/erp/tax-rates')
      setTaxRates(response.data.data || [])
    } catch (error) {
      console.error('Error fetching tax rates:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSupplierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const supplierId = e.target.value
    const supplier = suppliers.find(s => s.id === supplierId)
    
    if (supplier) {
      setFormData(prev => ({
        ...prev,
        supplierId: supplier.id,
        supplier: supplier.name,
        supplierAddress: supplier.address || '',
        supplierTaxId: supplier.taxId || '',
        currency: supplier.currency || 'GBP',
        paymentTerms: supplier.paymentTerms || '',
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        supplierId: '',
        supplier: '',
        supplierAddress: '',
        supplierTaxId: '',
      }))
    }
  }

  const handleItemChange = (index: number, field: keyof PurchaseInvoiceItem, value: string) => {
    const newItems = [...formData.items]
    newItems[index] = { ...newItems[index], [field]: value }
    setFormData(prev => ({ ...prev, items: newItems }))
  }

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '' }],
    }))
  }

  const removeItem = (index: number) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      }))
    }
  }

  const calculateTotals = () => {
    try {
      let subtotal = 0
      let taxAmount = 0

      const safeTaxRates = taxRates || []

      formData.items.forEach(item => {
        try {
          const quantityStr = String(item.quantity || '0')
          const unitPriceStr = String(item.unitPrice || '0')
          const quantity = parseFloat(quantityStr) || 0
          const unitPrice = parseFloat(unitPriceStr) || 0
          
          if (isNaN(quantity) || isNaN(unitPrice)) {
            return
          }
          
          const itemTotal = quantity * unitPrice
          subtotal += itemTotal

          if (item.taxRateId && safeTaxRates.length > 0) {
            const taxRate = safeTaxRates.find((tr: any) => tr && tr.id === item.taxRateId)
            if (taxRate && taxRate.rate !== undefined && taxRate.rate !== null) {
              const rate = parseFloat(String(taxRate.rate)) || 0
              if (!isNaN(rate)) {
                taxAmount += itemTotal * (rate / 100)
              }
            }
          }
        } catch (err) {
          console.error('Error processing item:', err, item)
        }
      })

      return { subtotal, taxAmount, total: subtotal + taxAmount }
    } catch (error) {
      console.error('Error in calculateTotals:', error)
      return { subtotal: 0, taxAmount: 0, total: 0 }
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.supplier) {
      toast.error('Please enter supplier name')
      return
    }

    if (!formData.items || formData.items.length === 0 || formData.items.some(item => !item.description || !item.accountId)) {
      toast.error('Please fill in all required item fields')
      return
    }

    try {
      const payload = {
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        postingDate: formData.postingDate,
        supplier: formData.supplier,
        supplierAddress: formData.supplierAddress || undefined,
        supplierTaxId: formData.supplierTaxId || undefined,
        currency: formData.currency,
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(String(item.quantity || '0')) || 0,
          unitPrice: parseFloat(String(item.unitPrice || '0')) || 0,
          accountId: item.accountId,
          taxRateId: item.taxRateId || undefined,
        })),
      }

      await axios.post('/api/erp/purchase-invoices', payload)
      toast.success('Purchase invoice created successfully!')
      setShowCreateModal(false)
      setFormData({
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        postingDate: new Date().toISOString().split('T')[0],
        supplier: '',
        supplierAddress: '',
        supplierTaxId: '',
        currency: 'GBP',
        notes: '',
        terms: '',
        paymentTerms: '',
        items: [{ description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '' }],
      })
      fetchPurchaseInvoices()
    } catch (error: any) {
      console.error('Error creating purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to create purchase invoice')
    }
  }

  const handleView = async (invoiceId: string) => {
    setIsLoadingInvoice(true)
    try {
      const response = await axios.get(`/api/erp/purchase-invoices/${invoiceId}`)
      setSelectedInvoice(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching purchase invoice:', error)
      toast.error('Failed to load purchase invoice details')
    } finally {
      setIsLoadingInvoice(false)
    }
  }

  const handleEdit = async (invoice: PurchaseInvoice) => {
    setEditingInvoice(invoice)
    setFormData({
      invoiceDate: invoice.invoiceDate.split('T')[0],
      dueDate: invoice.dueDate.split('T')[0],
      postingDate: invoice.postingDate ? invoice.postingDate.split('T')[0] : new Date().toISOString().split('T')[0],
      supplier: invoice.supplier,
      supplierAddress: invoice.supplierAddress || '',
      supplierTaxId: invoice.supplierTaxId || '',
      currency: invoice.currency || 'GBP',
      notes: invoice.notes || '',
      terms: invoice.terms || '',
      paymentTerms: invoice.paymentTerms || '',
      items: invoice.items?.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        accountId: item.account?.code || '',
        taxRateId: '',
      })) || [{ description: '', quantity: '', unitPrice: '', accountId: '', taxRateId: '' }],
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingInvoice) return

    if (!formData.supplier) {
      toast.error('Please enter supplier name')
      return
    }

    if (!formData.items || formData.items.length === 0 || formData.items.some(item => !item.description || !item.accountId)) {
      toast.error('Please fill in all required item fields')
      return
    }

    try {
      const payload = {
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        postingDate: formData.postingDate,
        supplier: formData.supplier,
        supplierAddress: formData.supplierAddress || undefined,
        supplierTaxId: formData.supplierTaxId || undefined,
        currency: formData.currency,
        notes: formData.notes || undefined,
        terms: formData.terms || undefined,
        paymentTerms: formData.paymentTerms || undefined,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: parseFloat(String(item.quantity || '0')) || 0,
          unitPrice: parseFloat(String(item.unitPrice || '0')) || 0,
          accountId: item.accountId,
          taxRateId: item.taxRateId || undefined,
        })),
      }

      // Add supplierId if a supplier was selected
      if (formData.supplierId) {
        payload.supplierId = formData.supplierId
      }

      await axios.put(`/api/erp/purchase-invoices/${editingInvoice.id}`, payload)
      toast.success('Purchase invoice updated successfully!')
      setShowEditModal(false)
      setEditingInvoice(null)
      fetchPurchaseInvoices()
    } catch (error: any) {
      console.error('Error updating purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to update purchase invoice')
    }
  }

  const handleSubmit = async (invoiceId: string) => {
    try {
      await axios.post(`/api/erp/purchase-invoices/${invoiceId}/submit`)
      toast.success('Purchase invoice submitted for approval')
      fetchPurchaseInvoices()
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        const response = await axios.get(`/api/erp/purchase-invoices/${invoiceId}`)
        setSelectedInvoice(response.data.data)
      }
    } catch (error: any) {
      console.error('Error submitting purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to submit purchase invoice')
    }
  }

  const handleApprove = async (invoiceId: string) => {
    try {
      await axios.post(`/api/erp/purchase-invoices/${invoiceId}/approve`)
      toast.success('Purchase invoice approved')
      fetchPurchaseInvoices()
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        const response = await axios.get(`/api/erp/purchase-invoices/${invoiceId}`)
        setSelectedInvoice(response.data.data)
      }
    } catch (error: any) {
      console.error('Error approving purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to approve purchase invoice')
    }
  }

  const handlePost = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to post this purchase invoice? This will create GL entries and cannot be undone.')) {
      return
    }
    try {
      await axios.post(`/api/erp/purchase-invoices/${invoiceId}/post`)
      toast.success('Purchase invoice posted successfully')
      fetchPurchaseInvoices()
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        const response = await axios.get(`/api/erp/purchase-invoices/${invoiceId}`)
        setSelectedInvoice(response.data.data)
      }
    } catch (error: any) {
      console.error('Error posting purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to post purchase invoice')
    }
  }

  const handleDelete = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to delete this purchase invoice? This action cannot be undone.')) {
      return
    }
    try {
      await axios.delete(`/api/erp/purchase-invoices/${invoiceId}`)
      toast.success('Purchase invoice deleted successfully')
      fetchPurchaseInvoices()
      if (selectedInvoice && selectedInvoice.id === invoiceId) {
        setShowViewModal(false)
        setSelectedInvoice(null)
      }
    } catch (error: any) {
      console.error('Error deleting purchase invoice:', error)
      toast.error(error.response?.data?.error || 'Failed to delete purchase invoice')
    }
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_purchase_invoice', 'read')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to view purchase invoices.</p>
      </div>
    )
  }

  const totals = calculateTotals()

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Purchase Invoices</h1>
          <PageHelp pageIdentifier="purchase-invoices" title="Purchase Invoices" />
        </div>
        {hasPermission('erp_purchase_invoice', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Create Purchase Invoice
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-gray-600">Loading purchase invoices...</p>
      ) : purchaseInvoices.length === 0 ? (
        <p className="text-gray-600">No purchase invoices found. Create your first purchase invoice.</p>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchaseInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleView(invoice.id)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.invoiceNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{invoice.supplier}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(invoice.dueDate).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {parseFloat(invoice.total).toLocaleString('en-GB', { style: 'currency', currency: invoice.currency || 'GBP' })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      invoice.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'APPROVED' ? 'bg-blue-100 text-blue-800' :
                      invoice.status === 'SUBMITTED' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {invoice.status === 'DRAFT' && hasPermission('erp_purchase_invoice', 'write') && (
                        <>
                          <button onClick={() => handleEdit(invoice)} className="text-indigo-600 hover:text-indigo-900">Edit</button>
                          <button onClick={() => handleSubmit(invoice.id)} className="text-yellow-600 hover:text-yellow-900">Submit</button>
                          <button onClick={() => handleDelete(invoice.id)} className="text-red-600 hover:text-red-900">Delete</button>
                        </>
                      )}
                      {invoice.status === 'SUBMITTED' && hasPermission('erp_purchase_invoice', 'approve') && (
                        <button onClick={() => handleApprove(invoice.id)} className="text-green-600 hover:text-green-900">Approve</button>
                      )}
                      {(invoice.status === 'APPROVED' || invoice.status === 'DRAFT') && hasPermission('erp_purchase_invoice', 'post') && (
                        <button onClick={() => handlePost(invoice.id)} className="text-blue-600 hover:text-blue-900">Post</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal - Similar to Invoices but with supplier instead of clientOrg */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Create Purchase Invoice</h2>
            <form onSubmit={handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Invoice Date <span className="text-red-500">*</span>
                    <FieldHelp fieldIdentifier="invoice-date" />
                  </label>
                  <input
                    type="date"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Due Date <span className="text-red-500">*</span>
                    <FieldHelp fieldIdentifier="due-date" />
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Posting Date <span className="text-red-500">*</span>
                    <FieldHelp fieldIdentifier="posting-date" />
                  </label>
                  <input
                    type="date"
                    name="postingDate"
                    value={formData.postingDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Supplier <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Supplier name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Supplier Address</label>
                  <input
                    type="text"
                    name="supplierAddress"
                    value={formData.supplierAddress}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Supplier Tax ID</label>
                  <input
                    type="text"
                    name="supplierTaxId"
                    value={formData.supplierTaxId}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Items <span className="text-red-500">*</span></label>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700">Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Quantity</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Unit Price</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Account <span className="text-red-500">*</span></label>
                        <select
                          value={item.accountId}
                          onChange={(e) => handleItemChange(index, 'accountId', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">Select Account</option>
                          {accounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.code} - {account.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Tax Rate</label>
                        <select
                          value={item.taxRateId}
                          onChange={(e) => handleItemChange(index, 'taxRateId', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">No Tax</option>
                          {taxRates.map(taxRate => (
                            <option key={taxRate.id} value={taxRate.id}>
                              {taxRate.name} ({taxRate.rate}%)
                            </option>
                          ))}
                        </select>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="mt-2 text-red-600 hover:text-red-900 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-3 text-blue-600 hover:text-blue-900 text-sm font-medium"
                >
                  + Add Item
                </button>
                <div className="mt-4 text-right text-sm font-semibold">
                  <div>Subtotal: {totals.subtotal.toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</div>
                  <div>Tax: {totals.taxAmount.toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</div>
                  <div className="text-lg">Total: {totals.total.toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Create Purchase Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal - Similar structure to Invoices view modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Purchase Invoice: {selectedInvoice.invoiceNumber}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedInvoice(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 text-sm text-gray-600">
              <p><strong>Supplier:</strong> {selectedInvoice.supplier}</p>
              <p><strong>Invoice Date:</strong> {new Date(selectedInvoice.invoiceDate).toLocaleDateString()}</p>
              <p><strong>Due Date:</strong> {new Date(selectedInvoice.dueDate).toLocaleDateString()}</p>
              <p><strong>Status:</strong> {selectedInvoice.status}</p>
              <p><strong>Total:</strong> {parseFloat(selectedInvoice.total).toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}</p>
            </div>

            {selectedInvoice.items && selectedInvoice.items.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Items</h3>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedInvoice.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm text-gray-900">{item.description}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">{parseFloat(item.quantity).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">
                          {parseFloat(item.unitPrice).toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">
                          {parseFloat(item.taxAmount).toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500 text-right">
                          {parseFloat(item.total).toLocaleString('en-GB', { style: 'currency', currency: selectedInvoice.currency || 'GBP' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              {selectedInvoice.status === 'DRAFT' && hasPermission('erp_purchase_invoice', 'write') && (
                <>
                  <button onClick={() => { setShowViewModal(false); handleEdit(selectedInvoice); }} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
                    Edit
                  </button>
                  <button onClick={() => handleSubmit(selectedInvoice.id)} className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700">
                    Submit
                  </button>
                  <button onClick={() => handleDelete(selectedInvoice.id)} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                    Delete
                  </button>
                </>
              )}
              {selectedInvoice.status === 'SUBMITTED' && hasPermission('erp_purchase_invoice', 'approve') && (
                <button onClick={() => handleApprove(selectedInvoice.id)} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                  Approve
                </button>
              )}
              {(selectedInvoice.status === 'APPROVED' || selectedInvoice.status === 'DRAFT') && hasPermission('erp_purchase_invoice', 'post') && (
                <button onClick={() => handlePost(selectedInvoice.id)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Post
                </button>
              )}
              {(selectedInvoice.status === 'POSTED' || selectedInvoice.status === 'PARTIALLY_PAID') && hasPermission('erp_payment', 'write') && parseFloat(selectedInvoice.outstandingAmount) > 0 && (
                <button
                  onClick={async () => {
                    try {
                      // Get default bank account
                      const bankAccountsResponse = await axios.get('/api/erp/bank-accounts')
                      const bankAccount = bankAccountsResponse.data.data?.[0]

                      if (!bankAccount) {
                        toast.error('No bank account found. Please set up a bank account first.')
                        return
                      }

                      // Create payment with pre-filled data
                      await axios.post(`/api/erp/purchase-invoices/${selectedInvoice.id}/create-payment`, {
                        paymentDate: new Date().toISOString().split('T')[0],
                        postingDate: new Date().toISOString().split('T')[0],
                        amount: parseFloat(selectedInvoice.outstandingAmount),
                        paymentMethod: 'BANK_TRANSFER',
                        accountId: bankAccount.id,
                        reference: `Payment for ${selectedInvoice.invoiceNumber}`,
                        description: `Payment for purchase invoice ${selectedInvoice.invoiceNumber}`,
                      })

                      toast.success('Payment created successfully')
                      setShowViewModal(false)
                      setSelectedInvoice(null)
                      fetchPurchaseInvoices()
                    } catch (error: any) {
                      console.error('Error creating payment:', error)
                      toast.error(error.response?.data?.error || 'Failed to create payment')
                    }
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Make Payment
                </button>
              )}
              {selectedInvoice.status === 'POSTED' && hasPermission('erp_purchase_invoice', 'write') && (
                <button
                  type="button"
                  onClick={async () => {
                    const reason = prompt('Enter reason for amendment (optional):')
                    try {
                      const response = await axios.post(`/api/erp/purchase-invoices/${selectedInvoice.id}/amend`, { reason: reason || undefined })
                      toast.success('Purchase invoice amended successfully')
                      setShowViewModal(false)
                      setSelectedInvoice(null)
                      fetchInvoices()
                    } catch (error: any) {
                      console.error('Error amending purchase invoice:', error)
                      toast.error(error.response?.data?.error || 'Failed to amend purchase invoice')
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>Amend</span>
                </button>
              )}
              {hasPermission('erp_purchase_invoice', 'write') && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const response = await axios.post(`/api/erp/purchase-invoices/${selectedInvoice.id}/duplicate`)
                      toast.success('Purchase invoice duplicated successfully')
                      setShowViewModal(false)
                      setSelectedInvoice(null)
                      fetchInvoices()
                    } catch (error: any) {
                      console.error('Error duplicating purchase invoice:', error)
                      toast.error(error.response?.data?.error || 'Failed to duplicate purchase invoice')
                    }
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Duplicate</span>
                </button>
              )}
              {selectedInvoice.status === 'POSTED' && hasPermission('erp_purchase_invoice', 'write') && (
                <button
                  type="button"
                  onClick={async () => {
                    const reason = prompt('Enter reason for cancellation (optional):')
                    try {
                      await axios.post(`/api/erp/purchase-invoices/${selectedInvoice.id}/cancel`, { reason: reason || undefined })
                      toast.success('Purchase invoice cancelled successfully')
                      setShowViewModal(false)
                      setSelectedInvoice(null)
                      fetchInvoices()
                    } catch (error: any) {
                      console.error('Error cancelling purchase invoice:', error)
                      toast.error(error.response?.data?.error || 'Failed to cancel purchase invoice')
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
              )}
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedInvoice(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar to create modal */}
      {showEditModal && editingInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Edit Purchase Invoice</h2>
            <form onSubmit={handleUpdate}>
              {/* Same form fields as create modal */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Invoice Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Due Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Posting Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    name="postingDate"
                    value={formData.postingDate}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Supplier <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="supplier"
                  value={formData.supplier}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Items section - same as create modal */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Items <span className="text-red-500">*</span></label>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700">Description</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Quantity</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Unit Price</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Account <span className="text-red-500">*</span></label>
                        <select
                          value={item.accountId}
                          onChange={(e) => handleItemChange(index, 'accountId', e.target.value)}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">Select Account</option>
                          {accounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.code} - {account.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Tax Rate</label>
                        <select
                          value={item.taxRateId}
                          onChange={(e) => handleItemChange(index, 'taxRateId', e.target.value)}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="">No Tax</option>
                          {taxRates.map(taxRate => (
                            <option key={taxRate.id} value={taxRate.id}>
                              {taxRate.name} ({taxRate.rate}%)
                            </option>
                          ))}
                        </select>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="mt-2 text-red-600 hover:text-red-900 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-3 text-blue-600 hover:text-blue-900 text-sm font-medium"
                >
                  + Add Item
                </button>
                <div className="mt-4 text-right text-sm font-semibold">
                  <div>Subtotal: {totals.subtotal.toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</div>
                  <div>Tax: {totals.taxAmount.toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</div>
                  <div className="text-lg">Total: {totals.total.toLocaleString('en-GB', { style: 'currency', currency: formData.currency || 'GBP' })}</div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingInvoice(null)
                  }}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Update Purchase Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default PurchaseInvoices
